import { useHistory, useLocation } from 'react-router-dom';

export type BillingGroupBy = 'byDay' | 'byWeek' | 'byMonth';

/**
 * Custom hook to manage groupBy state via URL query parameters for billing pages.
 * Synchronizes groupBy state across Billing, Expenses, InVsOut, and Payments tabs.
 *
 * @returns [groupBy, setGroupBy] tuple where:
 *  - groupBy: current grouping mode from URL or default ('byDay')
 *  - setGroupBy: function to update groupBy in URL query params
 */
export const useBillingGroupBy = (): [BillingGroupBy, (newGroupBy: BillingGroupBy) => void] => {
    const history = useHistory();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const groupByFromUrl = queryParams.get('groupBy') as BillingGroupBy | null;
    const validGroupByValues: BillingGroupBy[] = ['byDay', 'byWeek', 'byMonth'];
    const groupBy: BillingGroupBy = groupByFromUrl && validGroupByValues.includes(groupByFromUrl)
        ? groupByFromUrl
        : 'byDay';

    const setGroupBy = (newGroupBy: BillingGroupBy) => {
        const params = new URLSearchParams(location.search);
        params.set('groupBy', newGroupBy);
        history.push({ search: params.toString() });
    };

    return [groupBy, setGroupBy];
};
